import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import styles from '../../../assets/styles/LayoutShared.module.css';
import Button from '../../../components/Button/Button';
import { Settings } from '@mui/icons-material';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Resumo {
  totalDespesas: number;
  totalReceitas: number;
  saldo: number;
}
interface EvolucaoMensal {
  mes: string;
  despesas: number;
  receitas: number;
}
interface Categoria {
  categoria: string;
  total: number;
}
interface UltimoEvento {
  uid_evento: string;
  titulo: string;
  categoria: string;
  valor: number;
  data_evento: string;
  status_kanban: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMOJI_CATEGORIA: Record<string, string> = {
  operacao_agricola: '🚜', custo: '💸', receita: '💰',
  manutencao: '🔧', abastecimento: '⛽', outros: '📝',
};
const LABEL_CATEGORIA: Record<string, string> = {
  operacao_agricola: 'Operação Agrícola', custo: 'Custo', receita: 'Receita',
  manutencao: 'Manutenção', abastecimento: 'Abastecimento', outros: 'Outros',
};
const COLORS_PIZZA = ['#e74c3c','#e67e22','#f39c12','#3498db','#9b59b6','#1abc9c'];

const formatBRL = (val: number) =>
  val?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? 'R$ 0,00';

const formatMes = (mes: string) => {
  const [ano, m] = mes.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(m)-1]}/${ano.slice(2)}`;
};

const formatData = (raw: string) => {
  const d = new Date(raw);
  return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}`;
};

const getIntervalo = (opcao: string) => {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const pad = (n: number) => String(n).padStart(2,'0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  switch (opcao) {
    case 'mes_atual': return { inicio: `${ano}-${pad(mes+1)}-01`, fim: fmt(new Date(ano, mes+1, 0)) };
    case 'mes_passado': return { inicio: `${ano}-${pad(mes)}-01`, fim: fmt(new Date(ano, mes, 0)) };
    case 'trimestre': return { inicio: fmt(new Date(ano, mes-2, 1)), fim: fmt(new Date(ano, mes+1, 0)) };
    case 'ano':
    default: return { inicio: `${ano}-01-01`, fim: `${ano}-12-31` };
  }
};

// ─── Componente ───────────────────────────────────────────────────────────────

const AgroFinanceiro = () => {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState('ano');
  const [datas, setDatas] = useState(getIntervalo('ano'));
  const [loading, setLoading] = useState(true);

  const [resumo, setResumo] = useState<Resumo>({ totalDespesas: 0, totalReceitas: 0, saldo: 0 });
  const [evolucao, setEvolucao] = useState<EvolucaoMensal[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [ultimosEventos, setUltimosEventos] = useState<UltimoEvento[]>([]);

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
    const params = new URLSearchParams({
      dataInicio: datas.inicio,
      dataFim: datas.fim,
      ...(uidFazenda && { uid_unidade_producao: uidFazenda }),
    });
    api.get(`/agro/dashboard?${params}`)
      .then(res => {
        const d = res.data.data;
        setResumo(d.resumo);
        setEvolucao(d.evolucaoMensal);
        setCategorias(d.breakdownCategoria.filter((c: Categoria) => c.total > 0));
        setUltimosEventos(d.ultimosEventos);
      })
      .finally(() => setLoading(false));
  }, [datas]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handlePeriodo = (p: string) => {
    setPeriodo(p);
    setDatas(getIntervalo(p));
  };

  const saldoPositivo = resumo.saldo >= 0;

  return (
    <div className={styles.container}>

      {/* ── CSS Responsivo ─────────────────────────────────────────────────── */}
      <style>{`
        @media (max-width: 768px) {
          .fin-quick-access { grid-template-columns: 1fr !important; gap: 10px !important; }
          .fin-header        { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .fin-period-btns   { flex-wrap: wrap !important; gap: 4px !important; }
          .fin-period-btns button { padding: 4px 10px !important; font-size: 0.72rem !important; }
          .fin-kpi-grid      { grid-template-columns: 1fr !important; gap: 10px !important; }
          .fin-chart-grid    { grid-template-columns: 1fr !important; gap: 12px !important; }
          /* Tabela: oculta coluna Categoria no mobile */
          .fin-col-hide      { display: none !important; }
        }
      `}</style>

      {/* Cabeçalho */}
      <div className="fin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className={styles.greenTitle}>Painel Financeiro</h1>
        <div className="fin-period-btns" style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'mes_atual', label: 'Este mês' },
            { key: 'mes_passado', label: 'Mês passado' },
            { key: 'trimestre', label: 'Trimestre' },
            { key: 'ano', label: 'Este ano' },
          ].map(op => (
            <button key={op.key} onClick={() => handlePeriodo(op.key)} style={{
              padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.8rem',
              background: periodo === op.key ? '#00b37e' : '#f0f0f0',
              color: periodo === op.key ? '#fff' : '#555',
            }}>{op.label}</button>
          ))}
        </div>
        <Button
          variant='contain'
          className={styles.btnConfig}
          onClick={() => navigate("/agro/financialHub")}
          title="Configurações financeiras"
        >
          <Settings />
        </Button>
      </div>

      {/* 3 Cards */}
      <div className="fin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 24, opacity: loading ? 0.6 : 1 }}>
        <FinCard label="Total Gasto" value={formatBRL(resumo.totalDespesas)} icon="💸" color="#e74c3c" bgColor="#fff5f5" />
        <FinCard label="Total Recebido" value={formatBRL(resumo.totalReceitas)} icon="💰" color="#00b37e" bgColor="#f0fff8" />
        <FinCard
          label="Saldo" value={formatBRL(resumo.saldo)}
          icon={saldoPositivo ? '📈' : '📉'}
          color={saldoPositivo ? '#00b37e' : '#e74c3c'}
          bgColor={saldoPositivo ? '#f0fff8' : '#fff5f5'}
          destaque
        />
      </div>

      {/* Acesso Rápido */}
      <div className="fin-quick-access" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <button onClick={() => navigate("/agro/financialHub/accountsPayable")} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, padding: '20px 12px', borderRadius: 16, border: '2px solid #e74c3c',
          background: '#fff', cursor: 'pointer', transition: 'all 0.18s',
          boxShadow: '0 2px 8px rgba(231,76,60,0.10)',
        }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fff5f5'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; (e.currentTarget as HTMLElement).style.transform = ''; }}
        >
          <span style={{ fontSize: '2rem' }}>💸</span>
          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#e74c3c', textAlign: 'center' }}>Contas a Pagar</span>
        </button>

        <button onClick={() => navigate("/agro/financialHub/accountsReceivable")} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, padding: '20px 12px', borderRadius: 16, border: '2px solid #00b37e',
          background: '#fff', cursor: 'pointer', transition: 'all 0.18s',
          boxShadow: '0 2px 8px rgba(0,179,126,0.10)',
        }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f0fff8'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; (e.currentTarget as HTMLElement).style.transform = ''; }}
        >
          <span style={{ fontSize: '2rem' }}>💰</span>
          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#00b37e', textAlign: 'center' }}>Contas a Receber</span>
        </button>

        <button onClick={() => navigate("/agro/financialHub/cashFlow")} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, padding: '20px 12px', borderRadius: 16, border: '2px solid #3498db',
          background: '#fff', cursor: 'pointer', transition: 'all 0.18s',
          boxShadow: '0 2px 8px rgba(52,152,219,0.10)',
        }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f0f8ff'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; (e.currentTarget as HTMLElement).style.transform = ''; }}
        >
          <span style={{ fontSize: '2rem' }}>📈</span>
          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#3498db', textAlign: 'center' }}>Fluxo de Caixa</span>
        </button>
      </div>

      {/* Gráficos */}
      <div className="fin-chart-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Evolução Mensal — Despesas vs Receitas</div>
          <div style={{ height: 280, padding: '16px 8px 8px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={evolucao} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="mes" tickFormatter={formatMes} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: any) => formatBRL(v)} labelFormatter={(label: any) => formatMes(String(label))} />
                <Bar dataKey="despesas" name="Despesas" fill="#e74c3c" radius={[4,4,0,0]} barSize={18} />
                <Bar dataKey="receitas" name="Receitas" fill="#00b37e" radius={[4,4,0,0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Por Categoria</div>
          <div style={{ height: 280, padding: '16px 0 8px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categorias}
                  dataKey="total"
                  nameKey="categoria"
                  cx="50%" cy="50%"
                  outerRadius={90}
                  label={({ name, percent }: any) =>
                    `${EMOJI_CATEGORIA[name] || '📝'} ${((percent||0)*100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {categorias.map((_, i) => <Cell key={i} fill={COLORS_PIZZA[i % COLORS_PIZZA.length]} />)}
                </Pie>
                <Tooltip formatter={(v: any, name: any) => [formatBRL(v), LABEL_CATEGORIA[name] || name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Últimos eventos — com scroll e clique */}
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>Últimos Registros</div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          <table className={styles.table} style={{ marginTop: 0 }}>
            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <tr>
                <th style={{ width: '40%' }}>Título</th>
                <th className="fin-col-hide">Categoria</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th style={{ textAlign: 'center' }}>Data</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {ultimosEventos.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                  Nenhum evento no período.
                </td></tr>
              )}
              {ultimosEventos.map(ev => (
                <tr
                  key={ev.uid_evento}
                  onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f8fffe')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <td>{EMOJI_CATEGORIA[ev.categoria] || '📝'} {ev.titulo}</td>
                  <td className="fin-col-hide">{LABEL_CATEGORIA[ev.categoria] || ev.categoria}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: ev.categoria === 'receita' ? '#00b37e' : '#333' }}>
                    {ev.valor > 0 ? formatBRL(ev.valor) : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatData(ev.data_evento)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <StatusBadge status={ev.status_kanban} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

const FinCard = ({ label, value, icon, color, bgColor, destaque }: any) => (
  <div style={{
    background: '#fff', padding: '24px', borderRadius: 16,
    boxShadow: `0 0 0 1.5px ${color}40, 0 0 18px ${color}28, 0 2px 8px rgba(0,0,0,0.05)`,
    display: 'flex', alignItems: 'center', gap: 20,
  }}>
    <div style={{ background: bgColor, padding: 14, borderRadius: 12, fontSize: '1.8rem', lineHeight: 1 }}>{icon}</div>
    <div>
      <p style={{ margin: 0, fontSize: '0.82rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
      <h2 style={{ margin: '4px 0 0', color, fontSize: destaque ? '1.7rem' : '1.5rem', fontWeight: 700 }}>{value}</h2>
    </div>
  </div>
);

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  registrado:   { label: 'Registrado',   color: '#3498db', bg: '#ebf5fb' },
  em_andamento: { label: 'Em andamento', color: '#f39c12', bg: '#fef9e7' },
  concluido:    { label: 'Concluído',    color: '#00b37e', bg: '#f0fff8' },
  cancelado:    { label: 'Cancelado',    color: '#999',    bg: '#f5f5f5' },
  agendado:     { label: 'Agendado',     color: '#9b59b6', bg: '#f5f0ff' },
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['registrado'];
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, padding: '3px 10px',
      borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
    }}>{cfg.label}</span>
  );
};

export default AgroFinanceiro;